import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Plus,
  Pencil,
  Trash2,
  Copy,
  BarChart3,
  Clock,
} from 'lucide-react';

interface CompanyConfig {
  id: string;
  name: string;
}

interface Script {
  id: string;
  company_config_id: string;
  script_type: string;
  title: string;
  content: string;
  version: number;
  is_active: boolean;
  usage_context: string | null;
  performance_metrics: any;
  created_at: string;
  updated_at: string;
}

const SCRIPT_TYPES = [
  { value: 'greeting', label: 'Greeting' },
  { value: 'qualification', label: 'Qualification' },
  { value: 'objection_handling', label: 'Objection Handling' },
  { value: 'closing', label: 'Closing' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'custom', label: 'Custom' },
];

export default function AgentScriptsPage() {
  const [agents, setAgents] = useState<CompanyConfig[]>([]);
  const [scripts, setScripts] = useState<Script[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingScript, setEditingScript] = useState<Script | null>(null);
  const [loading, setLoading] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    company_config_id: '',
    script_type: 'greeting',
    title: '',
    content: '',
    usage_context: '',
    is_active: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load agents (company_configs)
      const { data: agentsData, error: agentsError } = await supabase
        .from('company_configs')
        .select('id, name')
        .eq('is_active', true)
        .order('name');

      if (agentsError) throw agentsError;
      setAgents(agentsData || []);

      // Load scripts
      const { data: scriptsData, error: scriptsError } = await supabase
        .from('agent_scripts')
        .select('*')
        .order('created_at', { ascending: false });

      if (scriptsError) throw scriptsError;
      setScripts(scriptsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredScripts = scripts.filter((script) => {
    if (selectedAgent !== 'all' && script.company_config_id !== selectedAgent) return false;
    if (selectedType !== 'all' && script.script_type !== selectedType) return false;
    return true;
  });

  const openCreateModal = () => {
    setEditingScript(null);
    setFormData({
      company_config_id: agents[0]?.id || '',
      script_type: 'greeting',
      title: '',
      content: '',
      usage_context: '',
      is_active: true,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (script: Script) => {
    setEditingScript(script);
    setFormData({
      company_config_id: script.company_config_id,
      script_type: script.script_type,
      title: script.title,
      content: script.content,
      usage_context: script.usage_context || '',
      is_active: script.is_active,
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingScript) {
        // Update existing script
        const { error } = await supabase
          .from('agent_scripts')
          .update({
            ...formData,
            updated_at: new Date().toISOString(),
            version: editingScript.version + 1,
          })
          .eq('id', editingScript.id);

        if (error) throw error;
      } else {
        // Create new script
        const { error } = await supabase
          .from('agent_scripts')
          .insert([formData]);

        if (error) throw error;
      }

      setIsModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving script:', error);
      alert('Error saving script. Please try again.');
    }
  };

  const handleDelete = async (scriptId: string) => {
    if (!confirm('Are you sure you want to delete this script?')) return;

    try {
      const { error } = await supabase
        .from('agent_scripts')
        .delete()
        .eq('id', scriptId);

      if (error) throw error;
      loadData();
    } catch (error) {
      console.error('Error deleting script:', error);
      alert('Error deleting script. Please try again.');
    }
  };

  const handleDuplicate = async (script: Script) => {
    try {
      const { error } = await supabase
        .from('agent_scripts')
        .insert([{
          company_config_id: script.company_config_id,
          script_type: script.script_type,
          title: `${script.title} (Copy)`,
          content: script.content,
          usage_context: script.usage_context,
          is_active: false,
        }]);

      if (error) throw error;
      loadData();
    } catch (error) {
      console.error('Error duplicating script:', error);
      alert('Error duplicating script. Please try again.');
    }
  };

  const getAgentName = (agentId: string) => {
    return agents.find((a) => a.id === agentId)?.name || 'Unknown Agent';
  };

  const getScriptTypeBadge = (type: string) => {
    const colors: { [key: string]: string } = {
      greeting: 'bg-blue-100 text-blue-800',
      qualification: 'bg-green-100 text-green-800',
      objection_handling: 'bg-yellow-100 text-yellow-800',
      closing: 'bg-purple-100 text-purple-800',
      transfer: 'bg-orange-100 text-orange-800',
      emergency: 'bg-red-100 text-red-800',
      custom: 'bg-gray-100 text-gray-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[type] || colors.custom}`}>
        {SCRIPT_TYPES.find((t) => t.value === type)?.label || type}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading scripts...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Agent Scripts</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage call scripts for your AI voice agents with version control and performance tracking.
        </p>
      </div>

      <div className="mb-6 p-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-900">
        <p className="text-sm">
          About this page: Manage per-company call scripts stored in the <span className="font-mono">agent_scripts</span> table.
          Outbound content may be auto-seeded from <span className="font-mono">outbound-scripts.md</span> by the seeding utility.
          Keep canonical script/knowledge in <span className="font-mono">inbound-scripts-knowledge.md</span> and <span className="font-mono">outbound-scripts.md</span>, then use the
          <a href="/ai-voice-agents/compiler" className="underline font-medium"> Scripts Compiler</a> to build the final system prompt for ElevenLabs.
        </p>
      </div>

      {/* Filters and Actions */}
      <div className="mb-6 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-4">
          {/* Agent Filter */}
          <select
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
            className="rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="all">All Agents</option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </select>

          {/* Type Filter */}
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            {SCRIPT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Script
        </button>
      </div>

      {/* Scripts Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
        {filteredScripts.map((script) => (
          <div key={script.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{script.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">{getAgentName(script.company_config_id)}</p>
                </div>
                <div className="flex items-center gap-1">
                  {!script.is_active && (
                    <span className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-full">
                      Inactive
                    </span>
                  )}
                </div>
              </div>

              {/* Type Badge */}
              <div className="mb-4">
                {getScriptTypeBadge(script.script_type)}
              </div>

              {/* Content Preview */}
              <div className="mb-4">
                <p className="text-sm text-gray-600 line-clamp-3">{script.content}</p>
              </div>

              {/* Usage Context */}
              {script.usage_context && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-700">
                    <span className="font-medium">When to use:</span> {script.usage_context}
                  </p>
                </div>
              )}

              {/* Performance Metrics */}
              {script.performance_metrics && Object.keys(script.performance_metrics).length > 0 && (
                <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
                  <BarChart3 className="h-4 w-4" />
                  <span>Performance data available</span>
                </div>
              )}

              {/* Meta Info */}
              <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span>v{script.version}</span>
                </div>
                <div>
                  Updated {new Date(script.updated_at).toLocaleDateString()}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => openEditModal(script)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleDuplicate(script)}
                  className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  title="Duplicate"
                >
                  <Copy className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(script.id)}
                  className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredScripts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No scripts found. Create your first script to get started.</p>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                {editingScript ? 'Edit Script' : 'Create Script'}
              </h2>

              <div className="space-y-4">
                {/* Agent Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agent
                  </label>
                  <select
                    value={formData.company_config_id}
                    onChange={(e) => setFormData({ ...formData, company_config_id: e.target.value })}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select an agent</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Script Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Script Type
                  </label>
                  <select
                    value={formData.script_type}
                    onChange={(e) => setFormData({ ...formData, script_type: e.target.value })}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  >
                    {SCRIPT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="e.g., Standard Greeting for Business Hours"
                    required
                  />
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Script Content
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={10}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 font-mono text-sm"
                    placeholder="Enter the script content here..."
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Use [variables] for dynamic content, e.g., [customer_name], [technician_name]
                  </p>
                </div>

                {/* Usage Context */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Usage Context (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.usage_context}
                    onChange={(e) => setFormData({ ...formData, usage_context: e.target.value })}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="e.g., Use during business hours only"
                  />
                </div>

                {/* Active Status */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                    Active (script will be used by agent)
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  {editingScript ? 'Update Script' : 'Create Script'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
